import {DestroyRef, inject, provideAppInitializer} from '@angular/core';
import {Store} from '@ngxs/store';
import {WebSocketService, WebSocketState} from '../services/websocket.service';
import {AuthService} from '../services/auth.service';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {combineLatest, concatMap, distinctUntilChanged, filter, map, scan, switchMap, tap, timer} from 'rxjs';
import {OrgsState} from '../+state/orgs/state';
import {getApiUrl} from '../helpers/env.helper';

export const provideWsConnector = provideAppInitializer(() => {
  const authService = inject(AuthService);
  const store = inject(Store);
  const destroyRef = inject(DestroyRef);
  const ws = inject(WebSocketService);

  const isAuthenticated = toObservable(authService.authenticated);

  const token = combineLatest([isAuthenticated, authService.keycloakEvent]).pipe(
    filter(([isAuthenticated]) => isAuthenticated),
    map(() => authService.userToken()),
    distinctUntilChanged(),
    filter((token): token is string => !!token),
  )

  const orgId = store.select(OrgsState.selectedOrg);

  const exponentialReconnect = ws.connectionStatus$.pipe(
    // Track both OPEN and CLOSED states to reset counter on successful connection
    scan((acc, status) => {
      // Reset the counter when connection is open
      if (status === WebSocketState.OPEN) {
        return { attemptCount: 0, status };
      }
      // Increment counter when connection is closed
      if (status === WebSocketState.CLOSED) {
        return { attemptCount: acc.attemptCount + 1, status };
      }
      // Keep current state for other status values
      return { ...acc, status };
    }, { attemptCount: 0, status: WebSocketState.CLOSED }),
    // Only proceed with reconnection logic when closed
    filter(({ status }) => status === WebSocketState.CLOSED),
    tap(() => {
      console.log('WebSocket connection closed, reconnecting...');
    }),
    concatMap(({ attemptCount }) => {
      const delay = attemptCount === 0 ? 0 : Math.min(1000 * Math.pow(2, attemptCount - 1), 30000);
      return timer(delay).pipe(
        tap(() => {
          console.log(`Reconnecting attempt ${attemptCount + 1} after ${delay}ms delay`);
        })
      );
    }),
  );


  combineLatest([token, orgId, getApiUrl('ws', 'wss'), exponentialReconnect]).pipe(
    map(([token, orgId, url]) => {
      const tokenFromService = authService.userToken();
      const _token = token !== tokenFromService ? tokenFromService : token;
      return `${url}/stream?token=${_token}&org_id=${orgId}`;
    }),
    switchMap(url => ws.connect(url)),
    takeUntilDestroyed(destroyRef)
  ).subscribe();
});
